import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import Map, { NavigationControl, Source, Layer } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Box, Paper, Typography, IconButton, useTheme, Chip } from '@mui/material'
import { useCurrency } from '../../contexts/CurrencyContext'
import CloseIcon from '@mui/icons-material/Close'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import MapMarker from './MapMarker'
import MapConnectionLayer, { getBezierCoordinates } from './MapConnectionLayer'
import MapSimulationLayer from './MapSimulationLayer'
import MapControls from './MapControls'
import MapSearchBar from './MapSearchBar'
import MapMinimap from './MapMinimap'
import MapClusterLayer, { CLUSTER_ZOOM_THRESHOLD } from './MapClusterLayer'
import MapDemandMarker from './MapDemandMarker'
import MapDemandArcLayer from './MapDemandArcLayer'

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN

const placementIcons = {
  supplier: WarehouseIcon,
  factory: FactoryIcon,
  distributor: LocalShippingIcon,
  retailer: StorefrontIcon,
}

const placementColors = {
  supplier: '#10b981',
  factory: '#f59e0b',
  distributor: '#3b82f6',
  retailer: '#8b5cf6',
}

const INITIAL_VIEW_STATE = {
  longitude: 78.9629,
  latitude: 20.5937,
  zoom: 4.5,
  pitch: 0,
  bearing: 0,
}

const MAP_STYLE_URLS = {
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  navigation: 'mapbox://styles/mapbox/navigation-day-v1',
  dark: 'mapbox://styles/mapbox/dark-v11',
}

// Custom scale bar — avoids CSS conflicts that break the built-in ScaleControl
function MapScaleBar({ viewState, isDark }) {
  const lat = viewState.latitude || 20
  const zoom = viewState.zoom || 4

  const metersPerPixel = (40075016.686 * Math.cos((lat * Math.PI) / 180)) / (256 * Math.pow(2, zoom))
  const targetMeters = metersPerPixel * 90

  let label, barWidth
  if (targetMeters >= 1000) {
    const step = Math.pow(10, Math.floor(Math.log10(targetMeters / 1000)))
    const km = Math.round(targetMeters / 1000 / step) * step
    label = `${km} km`
    barWidth = (km * 1000) / metersPerPixel
  } else {
    const step = Math.pow(10, Math.floor(Math.log10(targetMeters)))
    const m = Math.round(targetMeters / step) * step
    label = `${m} m`
    barWidth = m / metersPerPixel
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 32,
        left: 60,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        pointerEvents: 'none',
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 600,
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)',
          textShadow: isDark ? '0 1px 3px rgba(0,0,0,0.8)' : '0 1px 3px rgba(255,255,255,0.8)',
          mb: 0.25,
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: barWidth,
          height: 3,
          bgcolor: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.65)',
          borderLeft: `2px solid ${isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.65)'}`,
          borderRight: `2px solid ${isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.65)'}`,
          boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.6)' : '0 1px 4px rgba(255,255,255,0.6)',
        }}
      />
    </Box>
  )
}

function MapView({
  nodes,
  edges,
  onNodeClick,
  onEdgeClick,
  onNodePlace,
  onNodeMove,
  onConnect,
  selectedNode,
  themeMode,
  simulationResults,
  showSimAnimation,
  placementMode,
  onCancelPlacement,
  onViewNodeResults,
  visibility,
  demands = [],
  onDemandClick,
  selectedDemandId,
}) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { currency } = useCurrency()
  const mapRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // Controlled viewport state — needed for minimap sync, cluster tracking, and scale bar
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE)

  const [connectMode, setConnectMode] = useState({ active: false, source: null })
  const [cursorPosition, setCursorPosition] = useState(null)
  const [edgeTooltip, setEdgeTooltip] = useState(null)

  // Feature states
  const [showMinimap, setShowMinimap] = useState(false)
  const [showTerrain, setShowTerrain] = useState(false)
  const [mapStyleType, setMapStyleType] = useState(null) // null = default dark/light

  // Fallback if visibility not passed from parent
  const vis = visibility || { supplier: true, factory: true, distributor: true, retailer: true, demand: true, labels: true, connections: true }

  // Group demands by target node for offset calculation
  const demandsByTarget = useMemo(() => {
    const map = {}
    demands.forEach(d => {
      if (!map[d.target_node]) map[d.target_node] = []
      map[d.target_node].push(d)
    })
    return map
  }, [demands])

  // Stores user-dragged lat/lng positions for demand markers {demandId: {lat, lng}}
  // Persisted to localStorage so positions survive page reloads
  const [demandPositions, setDemandPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snw_mapDemandPos') || '{}') } catch { return {} }
  })
  useEffect(() => {
    localStorage.setItem('snw_mapDemandPos', JSON.stringify(demandPositions))
  }, [demandPositions])

  const handleDemandDragEnd = useCallback((demandId, lat, lng) => {
    setDemandPositions(prev => ({ ...prev, [demandId]: { lat, lng } }))
  }, [])

  // Which node IDs are inside cluster bubbles (hidden from React Markers)
  const [clusteredNodeIds, setClusteredNodeIds] = useState(new Set())

  const effectiveMapStyle = mapStyleType
    ? MAP_STYLE_URLS[mapStyleType]
    : isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11'

  const mapNodes = useMemo(
    () => nodes.filter(n => n.data.lat != null && n.data.lng != null),
    [nodes]
  )

  const nodeStatsMap = useMemo(() => {
    if (!simulationResults?.individual_stats) return {}
    const map = {}
    Object.entries(simulationResults.individual_stats).forEach(([nodeId, data]) => {
      const s = data.stats
      const fulfilled = s.demand_fulfilled?.[1] || 0
      const received = s.demand_received?.[1] || 0
      const fillRate = received > 0 ? Math.round((fulfilled / received) * 100) : null
      const shortage = s.orders_shortage?.[1] || 0
      const shortageRatio = received > 0 ? shortage / received : 0
      map[nodeId] = {
        fillRate,
        profit: s.profit ?? null,
        inventoryLevel: s.inventory_level ?? null,
        isBottleneck: (fillRate !== null && fillRate < 50) || (s.profit != null && s.profit < 0) || shortageRatio > 0.2,
      }
    })
    return map
  }, [simulationResults])

  const sourceNode = useMemo(() => {
    if (!connectMode.source) return null
    return mapNodes.find(n => n.id === connectMode.source)
  }, [connectMode.source, mapNodes])

  // Curved bezier temp-connect line
  const tempLineGeoJson = useMemo(() => {
    if (!sourceNode || !cursorPosition) return { type: 'FeatureCollection', features: [] }
    const start = [sourceNode.data.lng, sourceNode.data.lat]
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: getBezierCoordinates(start, cursorPosition) },
      }],
    }
  }, [sourceNode, cursorPosition])

  // 3D Terrain — applied imperatively; re-applied when map style changes
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return
    const map = mapRef.current.getMap()
    if (!map) return

    const applyTerrain = () => {
      if (!map.getSource('mapbox-dem')) {
        map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        })
      }
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
      if (!map.getLayer('sky')) {
        map.addLayer({
          id: 'sky',
          type: 'sky',
          paint: { 'sky-type': 'atmosphere', 'sky-atmosphere-sun': [0.0, 0.0], 'sky-atmosphere-sun-intensity': 15 },
        })
      }
    }

    if (showTerrain) {
      if (map.isStyleLoaded()) {
        applyTerrain()
      } else {
        map.once('styledata', applyTerrain)
      }
      mapRef.current.flyTo({ pitch: 50, duration: 1000 })
    } else {
      map.setTerrain(null)
      if (map.getLayer('sky')) map.removeLayer('sky')
      if (map.getSource('mapbox-dem')) map.removeSource('mapbox-dem')
      mapRef.current.flyTo({ pitch: 0, duration: 800 })
    }
  }, [showTerrain, mapLoaded, mapStyleType]) // re-apply when style changes

  // Detect which nodes are inside cluster bubbles — update on map move/zoom
  const updateClusterState = useCallback(() => {
    if (!mapRef.current || !mapLoaded) return
    if (viewState.zoom >= CLUSTER_ZOOM_THRESHOLD) {
      setClusteredNodeIds(new Set())
      return
    }
    try {
      const unclusteredFeatures = mapRef.current.queryRenderedFeatures(undefined, {
        layers: ['unclustered-points-query'],
      })
      const unclusteredIds = new Set(unclusteredFeatures.map(f => f.properties?.id).filter(Boolean))
      const allIds = new Set(mapNodes.map(n => n.id))
      setClusteredNodeIds(new Set([...allIds].filter(id => !unclusteredIds.has(id))))
    } catch (_) {
      // Layer not ready yet
    }
  }, [mapLoaded, viewState.zoom, mapNodes])

  const handleMapClick = useCallback(
    event => {
      if (placementMode) {
        const { lng, lat } = event.lngLat
        onNodePlace(placementMode, lat, lng)
        return
      }

      if (connectMode.active) {
        setConnectMode({ active: false, source: null })
        setCursorPosition(null)
        return
      }

      // Check if a connection line was clicked — select it in config panel
      if (mapRef.current && mapLoaded && onEdgeClick) {
        const lineFeatures = mapRef.current.queryRenderedFeatures(event.point, {
          layers: ['connection-lines-main'],
        })
        if (lineFeatures?.length > 0) {
          const edgeId = lineFeatures[0].properties?.id
          const clickedEdge = edges.find(e => e.id === edgeId)
          if (clickedEdge) {
            onEdgeClick(event, clickedEdge)
            return
          }
        }
      }

      // Cluster mode — handle cluster expand or node select
      if (viewState.zoom < CLUSTER_ZOOM_THRESHOLD && mapRef.current && mapLoaded) {
        const features = mapRef.current.queryRenderedFeatures(event.point, {
          layers: ['clusters'],
        })
        if (features.length > 0 && features[0].properties.cluster) {
          mapRef.current.flyTo({ center: features[0].geometry.coordinates, zoom: viewState.zoom + 2, duration: 800 })
          return
        }
      }

      onNodeClick(null, null)
      onDemandClick?.(null)
    },
    [placementMode, onNodePlace, connectMode.active, onNodeClick, onEdgeClick, onDemandClick, edges, viewState.zoom, mapLoaded]
  )

  const handleMouseMove = useCallback(
    event => {
      if (connectMode.active && connectMode.source) {
        setCursorPosition([event.lngLat.lng, event.lngLat.lat])
      }
      if (mapRef.current && mapLoaded) {
        const features = mapRef.current.queryRenderedFeatures(event.point, { layers: ['connection-lines-main'] })
        if (features?.length > 0) {
          const props = features[0].properties
          setEdgeTooltip({ x: event.point.x, y: event.point.y, cost: props.edgeCost, leadTime: props.edgeLeadTime })
        } else {
          setEdgeTooltip(null)
        }
      }
    },
    [connectMode.active, connectMode.source, mapLoaded]
  )

  const handleMarkerClick = useCallback(
    node => {
      if (connectMode.active && connectMode.source) {
        if (connectMode.source !== node.id) {
          onConnect({ source: connectMode.source, target: node.id, sourceHandle: null, targetHandle: null })
        }
        setConnectMode({ active: false, source: null })
        setCursorPosition(null)
        return
      }
      onNodeClick(null, node)
    },
    [connectMode, onConnect, onNodeClick]
  )

  const handleStartConnect = useCallback(nodeId => {
    setConnectMode({ active: true, source: nodeId })
  }, [])

  const handleNodeMove = useCallback((nodeId, lat, lng) => onNodeMove(nodeId, lat, lng), [onNodeMove])

  const handleToggleConnectMode = useCallback(() => {
    setConnectMode(prev => {
      if (prev.active) { setCursorPosition(null); return { active: false, source: null } }
      return { active: true, source: null }
    })
  }, [])

  const handleMoveEnd = useCallback(() => {
    updateClusterState()
  }, [updateClusterState])

  const PlacementIcon = placementMode ? placementIcons[placementMode] : null
  const shouldShowAnimation = showSimAnimation || false
  const legendBottom = showMinimap ? 175 : 80

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onMoveEnd={handleMoveEnd}
        onClick={handleMapClick}
        onMouseMove={handleMouseMove}
        onLoad={() => { setMapLoaded(true); updateClusterState() }}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle={effectiveMapStyle}
        cursor={placementMode || connectMode.active ? 'crosshair' : 'grab'}
        attributionControl={false}
        antialias
        reuseMaps
      >
        <NavigationControl position="top-left" showCompass showZoom visualizePitch />

        {/* Cluster layer — visible only zoom < threshold */}
        {mapLoaded && (
          <MapClusterLayer nodes={mapNodes} themeMode={themeMode} />
        )}

        {/* Connection lines (curved bezier arcs) */}
        {mapLoaded && (
          <MapConnectionLayer nodes={mapNodes} edges={edges} themeMode={themeMode} simulationResults={simulationResults} showConnections={vis.connections} />
        )}

        {/* Temporary bezier connect line */}
        {mapLoaded && connectMode.source && cursorPosition && (
          <Source id="temp-connect-line" type="geojson" data={tempLineGeoJson}>
            <Layer
              id="temp-connect-line-glow"
              type="line"
              paint={{ 'line-color': isDark ? '#818cf8' : '#4f46e5', 'line-width': 5, 'line-opacity': 0.15, 'line-blur': 3 }}
              layout={{ 'line-cap': 'round' }}
            />
            <Layer
              id="temp-connect-line-main"
              type="line"
              paint={{ 'line-color': isDark ? '#a5b4fc' : '#6366f1', 'line-width': 2, 'line-opacity': 0.8, 'line-dasharray': [4, 3] }}
              layout={{ 'line-cap': 'round' }}
            />
          </Source>
        )}

        {/* Simulation flow animation (trucks along bezier arcs) */}
        {mapLoaded && shouldShowAnimation && (
          <MapSimulationLayer nodes={mapNodes} edges={edges} themeMode={themeMode} />
        )}

        {/* Node markers — hidden for nodes inside cluster bubbles or toggled off */}
        {mapNodes.map(node => {
          if (clusteredNodeIds.has(node.id)) return null
          const nodeType = node.data.nodeType || 'supplier'
          if (!vis[nodeType]) return null
          return (
            <MapMarker
              key={node.id}
              node={node}
              selected={selectedNode?.id === node.id}
              onClick={handleMarkerClick}
              onDragEnd={handleNodeMove}
              themeMode={themeMode}
              connectSource={connectMode.source}
              onStartConnect={handleStartConnect}
              nodeKpis={nodeStatsMap[node.id] || null}
              onViewNodeResults={onViewNodeResults}
              showLabel={vis.labels}
            />
          )
        })}

        {/* Demand / customer markers */}
        {vis.demand !== false && demands.map(demand => {
          const targetNode = mapNodes.find(n => n.id === demand.target_node)
          if (!targetNode) return null
          const siblings = demandsByTarget[demand.target_node] || []
          const index = siblings.indexOf(demand)
          const total = siblings.length
          return (
            <MapDemandMarker
              key={demand.id}
              demand={demand}
              targetNode={targetNode}
              index={index}
              total={total}
              showLabel={vis.labels}
              position={demandPositions[demand.id] || null}
              onDragEnd={handleDemandDragEnd}
              onDemandClick={onDemandClick}
              isSelected={selectedDemandId === demand.id}
            />
          )
        })}
      </Map>

      {/* Demand arc overlay — SVG bezier arcs from customer markers to their target nodes */}
      {mapLoaded && vis.demand !== false && (
        <MapDemandArcLayer
          mapRef={mapRef}
          demands={demands}
          nodes={mapNodes}
          demandsByTarget={demandsByTarget}
          demandPositions={demandPositions}
          visible={vis.demand !== false}
        />
      )}

      {/* Custom scale bar */}
      {mapLoaded && <MapScaleBar viewState={viewState} isDark={isDark} />}

      {/* Map controls */}
      <MapControls
        nodes={nodes}
        mapRef={mapRef}
        connectMode={connectMode}
        onToggleConnectMode={handleToggleConnectMode}
        showTerrain={showTerrain}
        onToggleTerrain={() => setShowTerrain(v => !v)}
        showMinimap={showMinimap}
        onToggleMinimap={() => setShowMinimap(v => !v)}
        mapStyleType={mapStyleType}
        onChangeMapStyle={setMapStyleType}
      />

      {/* Geocoding search bar */}
      <MapSearchBar mapRef={mapRef} isDark={isDark} placementMode={placementMode} onPlaceNode={onNodePlace} />


      {/* Minimap overlay */}
      {showMinimap && <MapMinimap viewState={viewState} nodes={mapNodes} themeMode={themeMode} />}

      {/* Edge hover tooltip */}
      {edgeTooltip && (
        <Box
          sx={{
            position: 'absolute',
            left: edgeTooltip.x + 12,
            top: edgeTooltip.y - 40,
            zIndex: 15,
            pointerEvents: 'none',
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.97)',
            backdropFilter: 'blur(12px)',
            borderRadius: '8px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            px: 1.5, py: 0.75,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.12)',
            display: 'flex', gap: 1.5, alignItems: 'center',
          }}
        >
          {edgeTooltip.cost !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>C:</Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{currency.symbol}{edgeTooltip.cost}</Typography>
            </Box>
          )}
          {edgeTooltip.leadTime !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>LT:</Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{edgeTooltip.leadTime}d</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Flow intensity legend — shown after simulation */}
      {simulationResults?.individual_stats && mapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            bottom: legendBottom,
            right: 16,
            zIndex: 5,
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderRadius: '10px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            px: 2, py: 1.5,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)',
            transition: 'bottom 0.3s ease',
          }}
        >
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
            Flow Intensity
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#6b7280' }}>Low</Typography>
            <Box sx={{ width: 60, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${isDark ? '#818cf8' : '#4f46e5'}33, ${isDark ? '#818cf8' : '#4f46e5'})` }} />
            <Typography sx={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#6b7280' }}>High</Typography>
          </Box>
        </Box>
      )}

      {/* Placement mode indicator */}
      {placementMode && PlacementIcon && (
        <Paper elevation={0} sx={{
          position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          px: 2.5, py: 1, borderRadius: '12px',
          bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Chip
            icon={<PlacementIcon sx={{ fontSize: 16, color: `${placementColors[placementMode]} !important` }} />}
            label={`Click on map to place ${placementMode}`}
            size="small"
            sx={{
              bgcolor: `${placementColors[placementMode]}15`,
              color: isDark ? '#e2e8f0' : '#1e293b',
              fontWeight: 600, fontSize: '12.5px',
              border: `1px solid ${placementColors[placementMode]}30`,
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
          <IconButton size="small" onClick={onCancelPlacement} sx={{ width: 24, height: 24, color: isDark ? '#94a3b8' : '#6b7280', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Paper>
      )}

      {/* Connect mode indicator */}
      {connectMode.active && (
        <Paper elevation={0} sx={{
          position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          px: 2.5, py: 1, borderRadius: '12px',
          bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isDark ? 'rgba(129,140,248,0.3)' : 'rgba(79,70,229,0.2)'}`,
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#a5b4fc' : '#4f46e5' }}>
            {connectMode.source ? 'Click a target node to connect — or click empty space to cancel' : 'Click source node'}
          </Typography>
          <IconButton size="small" onClick={handleToggleConnectMode} sx={{ width: 24, height: 24, color: isDark ? '#94a3b8' : '#6b7280', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Paper>
      )}

      {/* Simulation running indicator */}
      {shouldShowAnimation && (
        <Paper elevation={0} sx={{
          position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          px: 3, py: 1.5, borderRadius: '12px',
          bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isDark ? 'rgba(129,140,248,0.3)' : 'rgba(79,70,229,0.2)'}`,
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: isDark ? '#a5b4fc' : '#4f46e5',
            animation: 'sim-pulse 1s ease-in-out infinite',
            '@keyframes sim-pulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.4, transform: 'scale(0.8)' },
            },
          }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>
            Simulating supply chain flow...
          </Typography>
        </Paper>
      )}

      {/* Empty state */}
      {mapNodes.length === 0 && !placementMode && !connectMode.active && (
        <Box sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 5 }}>
          <Paper elevation={0} sx={{
            px: 3, py: 1.5, borderRadius: '12px',
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: isDark ? '#94a3b8' : '#6b7280', textAlign: 'center' }}>
              Use the <strong>Build</strong> menu to add supply chain nodes to the map
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export default React.memo(MapView)
